import React, { useState, useEffect, useCallback } from 'react';
import { HomepageBanner } from '../types';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface BannerSliderProps {
  banners: HomepageBanner[];
  autoRotateInterval?: number;
  className?: string;
}

const BannerSlider: React.FC<BannerSliderProps> = ({
  banners,
  autoRotateInterval = 5000,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter only active banners with valid images
  const activeBanners = banners.filter(banner => {
    const hasImage = banner.s3Url || banner.imagePath;
    return hasImage;
  });

  const goToNext = useCallback(() => {
    if (activeBanners.length <= 1) return;
    setCurrentIndex(prev => (prev + 1) % activeBanners.length);
  }, [activeBanners.length]);

  const goToPrevious = useCallback(() => {
    if (activeBanners.length <= 1) return;
    setCurrentIndex(prev => (prev - 1 + activeBanners.length) % activeBanners.length);
  }, [activeBanners.length]);

  const goToSlide = useCallback((index: number) => {
    if (index === currentIndex) return;
    setCurrentIndex(index);
  }, [currentIndex]);

  // Auto-rotate effect
  useEffect(() => {
    if (activeBanners.length <= 1) return;

    const timer = setInterval(() => {
      goToNext();
    }, autoRotateInterval);

    return () => clearInterval(timer);
  }, [activeBanners.length, autoRotateInterval, goToNext]);

  // Reset index if banners change
  useEffect(() => {
    if (currentIndex >= activeBanners.length) {
      setCurrentIndex(0);
    }
  }, [activeBanners.length, currentIndex]);

  if (!activeBanners.length) {
    return null;
  }

  // Get image URL - prefer S3 URL, fallback to legacy imagePath
  const getImageUrl = (banner: HomepageBanner): string => {
    if (banner.s3Url) return banner.s3Url;
    if (banner.imagePath) return `/uploads/${banner.imagePath}`;
    return '';
  };

  return (
    <div className={`relative w-full overflow-hidden group ${className}`}>
      {/* Main banner image */}
      <div className="relative w-full aspect-[16/9]">
        {activeBanners.map((banner, index) => {
          const imageUrl = getImageUrl(banner);
          return (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={imageUrl}
                alt={banner.title || `Banner ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />

              {/* Overlay with title/subtitle if present */}
              {(banner.title || banner.subtitle) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
                  <div className="p-6 md:p-10 text-white">
                    {banner.title && (
                      <h2 className="text-2xl md:text-4xl font-bold mb-2">
                        {banner.title}
                      </h2>
                    )}
                    {banner.subtitle && (
                      <p className="text-sm md:text-lg opacity-90">
                        {banner.subtitle}
                      </p>
                    )}
                    {banner.linkUrl && banner.linkText && (
                      <a
                        href={banner.linkUrl}
                        className="inline-block mt-4 px-6 py-2 bg-white text-black rounded-full hover:bg-opacity-90 transition-colors"
                      >
                        {banner.linkText}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation arrows - only show if more than 1 banner */}
      {activeBanners.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
            aria-label="Previous banner"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-800" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
            aria-label="Next banner"
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-800" />
          </button>
        </>
      )}

      {/* Dot indicators - only show if more than 1 banner */}
      {activeBanners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
          {activeBanners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
